import { ICourseData, CourseDataOrResponse } from '../types/course-data';

/**
 * Normalizes course data responses to always return an array of ICourseData
 * regardless of the API response format
 *
 * @param response The potentially inconsistent API response
 * @returns Normalized array of ICourseData
 */
export function normalizeCourseData(response: unknown): ICourseData[] {
  if (!response) {
    return [];
  }

  // Case 1: Response is an array of ICourseData
  if (Array.isArray(response)) {
    if (response.length === 0) {
      return [];
    }

    // Check if first item has a data property that's an array
    if ('data' in response[0] && Array.isArray(response[0].data)) {
      return response[0].data || [];
    }

    // It's already an array of ICourseData
    return response as ICourseData[];
  }

  // Case 2: Response is an object with a data property that's an array
  if (typeof response === 'object' && response !== null) {
    if ('data' in response && Array.isArray(response.data)) {
      return response.data;
    }
  }

  // Case 3: Response is a single ICourseData object
  if (typeof response === 'object' && response !== null && 'documentId' in response) {
    return [response as ICourseData];
  }

  // Fallback
  console.warn('Unexpected API response format:', response);
  return [];
}

/**
 * Type guard to check if an object is an ICourseData
 */
export function isCourseData(obj: unknown): obj is ICourseData {
  return typeof obj === 'object' && obj !== null && 'documentId' in obj && 'title' in obj;
}

/**
 * Type guard to check if an object is a CourseDataResponse
 */
export function isCourseDataResponse(obj: unknown): obj is { data: ICourseData[] } {
  return (
    typeof obj === 'object' && obj !== null && 'data' in obj && Array.isArray((obj as any).data)
  );
}

/**
 * Type-safe API response wrapper
 * Use this for API calls to ensure type safety
 * @param apiCall The async API call function
 * @returns Normalized array of ICourseData
 */
export async function safeCourseDataFetch<T>(apiCall: () => Promise<T>): Promise<ICourseData[]> {
  try {
    const response = await apiCall();
    return normalizeCourseData(response);
  } catch (error) {
    console.error('API call failed:', error);
    return [];
  }
}
